import React from "react";
import { useTranslation } from "react-i18next";

function Contact() {
  const { t } = useTranslation();

  const contacts = [
    {
      name: t("contact.contacts.0.name"),
      phone: t("contact.contacts.0.phone"),
      email: t("contact.contacts.0.email"),
      image: process.env.PUBLIC_URL + "/images/dirk.png",
      role: t("contact.president")
    },
    {
      name: t("contact.contacts.1.name"),
      phone: t("contact.contacts.1.phone"),
      email: t("contact.contacts.1.email"),
      image: process.env.PUBLIC_URL + "/images/mike.jpg",
      role: t("contact.vicePresident")
    },
  ];

  return (
    <section className="pt-12 pb-12 bg-white dark:bg-slate-800 transition-colors relative z-10" id="contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("contact.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
            {t("contact.description")}
          </p>
        </div>

        {/* Contact Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {contacts.map((contact, index) => (
            <div 
              key={index}
              className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 hover:shadow-md transition-all duration-200"
            >
              <div className="flex flex-col space-y-4">
                {/* Header: Image and Name */}
                <div className="flex items-start gap-4">
                  {/* Profile Image */}
                  <img
                    src={contact.image}
                    alt={t("contact.contactAlt", { name: contact.name })}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-primary dark:border-primary-light shadow-md flex-shrink-0 transition-transform duration-300 hover:scale-105"
                  />
                  
                  {/* Name and Role */}
                  <div className="flex-1 min-w-0 pt-1">
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                      {contact.name}
                    </h3>
                    <span className="inline-block px-3 py-1 text-xs font-semibold text-primary dark:text-primary-light bg-primary/10 dark:bg-primary-light/20 rounded-full mb-1">
                      {contact.role}
                    </span>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t("contact.address.line1")}
                    </p>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div className="w-full space-y-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                  {/* Phone */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                    <a 
                      href={`tel:${contact.phone}`} 
                      className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 text-primary dark:text-primary-light flex items-center justify-center hover:bg-primary dark:hover:bg-primary-light hover:text-white transition-colors"
                      title={contact.phone}
                    >
                      <i className="fas fa-phone text-sm"></i>
                    </a>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {contact.phone}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t("contact.phone")}
                      </p>
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                    <a 
                      href={`mailto:${contact.email}`} 
                      className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 text-primary dark:text-primary-light flex items-center justify-center hover:bg-primary dark:hover:bg-primary-light hover:text-white transition-colors"
                      title={contact.email}
                    >
                      <i className="fas fa-envelope text-sm"></i>
                    </a>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {contact.email}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t("contact.email")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Address Card */}
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 hover:shadow-md transition-all duration-200">
            <div className="flex flex-col space-y-4">
              {/* Header: Image and Title */}
              <div className="flex items-start gap-4">
                {/* Address Image */}
                <img
                  src={process.env.PUBLIC_URL + "/images/vereinsheim.jpg"}
                  alt="Vereinsheim"
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-primary dark:border-primary-light shadow-md flex-shrink-0 transition-transform duration-300 hover:scale-105"
                />
                
                {/* Title and Badge */}
                <div className="flex-1 min-w-0 pt-1">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    {t("contact.address.title")}
                  </h3>
                  <span className="inline-block px-3 py-1 text-xs font-semibold text-primary dark:text-primary-light bg-primary/10 dark:bg-primary-light/20 rounded-full mb-1">
                    {t("contact.headquaters")}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t("contact.address.line1")}
                  </p>
                </div>
              </div>
              
              {/* Address Info */}
              <div className="w-full space-y-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                {/* Street */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                  <a 
                    href="https://www.google.com/maps?q=Dorothea+Erxleben+Straße+4,+16321+Bernau+bei+Berlin"
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 text-primary dark:text-primary-light flex items-center justify-center hover:bg-primary dark:hover:bg-primary dark:hover:bg-primary-light hover:text-white transition-colors"
                    title="In Google Maps öffnen"
                  >
                    <i className="fas fa-road text-sm"></i>
                  </a>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {t("contact.address.line2")}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t("contact.street")}
                    </p>
                  </div>
                </div>
                
                {/* Place */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                  <a 
                    href="https://www.google.com/maps?q=16321+Bernau+bei+Berlin"
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 dark:bg-primary-light/20 text-primary dark:text-primary-light flex items-center justify-center hover:bg-primary dark:hover:bg-primary-light hover:text-white transition-colors"
                    title="In Google Maps öffnen"
                  >
                    <i className="fas fa-map-marker-alt text-sm"></i>
                  </a>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {t("contact.address.line3")}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t("contact.place")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="w-full h-full min-h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2417.9594481199447!2d13.622852977935878!3d52.69682577210808!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a9b6abf71102ed%3A0x58d1eb87dccf74fd!2sDorothea-Erxleben-Stra%C3%9Fe%204%2C%2016321%20Bernau%20bei%20Berlin!5e0!3m2!1sde!2sde!4v1732811482055!5m2!1sde!2sde"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "400px" }}
              allowFullScreen
              loading="lazy"
              title={t("eventLocation.mapTitle")}
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
